import type { IconName } from 'ag-grid-community'
import { createIcon, iconElement } from './dom'

/**
 * AG Grid Icon Configuration
 * Maps AG Grid icon names to Facade icons
 *
 * @see https://www.ag-grid.com/javascript-data-grid/custom-icons/
 * @see IconName type from ag-grid-community for complete list
 */

/**
 * Icon mapping type - maps AG Grid IconName to custom icon name
 * Functions create icons only when the grid needs them
 */
type IconMapping = Partial<Record<IconName, () => Element>>

/**
 * Create icon configuration for AG Grid
 * Returns a mapping of AG Grid icon names to Facade icon elements
 *
 * Each icon is created lazily via function to prevent unnecessary DOM creation
 * until the icon is actually needed by the grid
 */
export const createIconsConfig = (): IconMapping => ({
  // Column group expand/collapse
  columnGroupOpened: () => createIcon('caret-down'),
  columnGroupClosed: () => createIcon('caret-right'),

  // Column selection (checkboxes in column header)
  columnSelectClosed: () => createIcon('square'),
  columnSelectOpen: () => createIcon('check-square'),
  columnSelectIndeterminate: () => createIcon('minus-square'),

  // Accordion expand/collapse (advanced filter builder, etc.)
  accordionClosed: () => createIcon('caret-right'),
  accordionOpen: () => createIcon('caret-down'),
  accordionIndeterminate: () => createIcon('minus'),

  // Column menu actions
  columnMovePin: () => createIcon('push-pin'),
  columnMoveHide: () => createIcon('eye-slash'),
  columnMoveMove: () => createIcon('arrows-out-cardinal'),
  columnMoveLeft: () => createIcon('arrow-left'),
  columnMoveRight: () => createIcon('arrow-right'),
  columnMoveGroup: () => createIcon('rows'),
  columnMoveValue: () => createIcon('function'),
  columnMovePivot: () => createIcon('arrow-bend-right-down'),

  // Drag and drop indicators
  dropNotAllowed: () => createIcon('prohibit'),

  // Row group expand/collapse
  groupContracted: () => createIcon('caret-right'),
  groupExpanded: () => createIcon('caret-down'),

  // Set filter group expand/collapse
  setFilterGroupClosed: () => createIcon('caret-right'),
  setFilterGroupOpen: () => createIcon('caret-down'),
  setFilterGroupIndeterminate: () => createIcon('minus'),
  setFilterLoading: () => createIcon('spinner'),

  // General UI icons
  chart: () => createIcon('chart-line'),
  close: () => createIcon('x'),
  cancel: () => createIcon('x-circle'),
  check: () => createIcon('check'),

  // Pagination
  first: () => createIcon('caret-double-left'),
  previous: () => createIcon('caret-left'),
  next: () => createIcon('caret-right'),
  last: () => createIcon('caret-double-right'),

  // Linking
  linked: () => createIcon('link'),
  unlinked: () => createIcon('link-break'),

  // Color picker
  colorPicker: () => createIcon('palette'),

  // Loading states
  groupLoading: () => createIcon('spinner'),

  // Menu icons
  menu: () => createIcon('dots-three-vertical'),
  legacyMenu: () => createIcon('list'),
  loadingMenuItems: () => createIcon('spinner'),
  menuAlt: () => createIcon('dots-three'),

  // Filter icons
  filter: () => createIcon('funnel'),
  filterActive: () => createIcon('funnel-simple'),
  filterAdd: () => createIcon('plus-circle'),
  filterCardExpand: () => createIcon('caret-down'),
  filterCardCollapse: () => createIcon('caret-up'),
  filterCardEditing: () => createIcon('pencil-simple'),
  filterTab: () => createIcon('funnel'),

  // Tool panels
  filtersToolPanel: () => createIcon('funnel'),
  columns: () => createIcon('columns'),
  columnsToolPanel: () => createIcon('columns'),

  // Window controls
  maximize: () => createIcon('arrows-out'),
  minimize: () => createIcon('arrows-in'),

  // Menu pin
  menuPin: () => createIcon('push-pin'),
  menuValue: () => createIcon('function'),
  menuAddRowGroup: () => createIcon('plus'),
  menuRemoveRowGroup: () => createIcon('minus'),

  // Clipboard operations
  clipboardCopy: () => createIcon('copy'),
  clipboardCut: () => createIcon('scissors'),
  clipboardPaste: () => createIcon('clipboard'),

  // Pivot and grouping panels
  pivotPanel: () => createIcon('arrow-bend-right-down'),
  rowGroupPanel: () => createIcon('rows'),
  valuePanel: () => createIcon('function'),

  // Drag handles
  columnDrag: () => createIcon('dots-six-vertical'),
  rowDrag: () => createIcon('dots-six-vertical'),

  // Row pinning
  rowPin: () => createIcon('push-pin'),
  rowPinTop: () => createIcon('arrow-line-up'),
  rowPinBottom: () => createIcon('arrow-line-down'),
  rowUnpin: () => createIcon('push-pin-slash'),

  // Save and export
  save: () => createIcon('floppy-disk'),
  csvExport: () => createIcon('file-csv'),
  excelExport: () => createIcon('file-xls'),

  // Small directional arrows (smallLeft/smallDown/smallRight deprecated in v33+)
  smallUp: () => createIcon('caret-up'),

  // Select dropdowns
  selectOpen: () => iconElement('caret-down', 'xs'),
  richSelectOpen: () => iconElement('caret-down', 'xs'),
  richSelectRemove: () => iconElement('backspace', 'xs'),

  // Panel delimiters
  panelDelimiter: () => createIcon('caret-right'),
  panelDelimiterRtl: () => createIcon('caret-left'),

  // Sub menu
  subMenuOpen: () => createIcon('caret-right'),
  subMenuOpenRtl: () => createIcon('caret-left'),

  // Sorting icons
  sortAscending: () => createIcon('sort-ascending', 'regular', 'xs'),
  sortDescending: () => createIcon('sort-descending', 'regular', 'xs'),
  sortUnSort: () => createIcon('arrows-down-up', 'regular', 'xs'),

  // Advanced filter builder
  advancedFilterBuilder: () => createIcon('funnel'),
  advancedFilterBuilderDrag: () => createIcon('dots-six-vertical'),
  advancedFilterBuilderInvalid: () => createIcon('warning-circle'),
  advancedFilterBuilderMoveUp: () => createIcon('arrow-up'),
  advancedFilterBuilderMoveDown: () => createIcon('arrow-down'),
  advancedFilterBuilderAdd: () => createIcon('plus-circle'),
  advancedFilterBuilderRemove: () => createIcon('x-circle'),
  advancedFilterBuilderSelectOpen: () => createIcon('caret-down'),

  // Charts menu
  chartsMenu: () => createIcon('chart-line'),
  chartsMenuEdit: () => createIcon('pencil-simple'),
  chartsMenuAdvancedSettings: () => createIcon('gear'),
  chartsMenuAdd: () => createIcon('plus-circle'),
  chartsColorPicker: () => createIcon('palette'),
  chartsThemePrevious: () => createIcon('caret-left'),
  chartsThemeNext: () => createIcon('caret-right'),
  chartsDownload: () => createIcon('download-simple'),

  // Ensure column visible (scroll to column)
  ensureColumnVisible: () => createIcon('eye'),

  // Checkboxes
  checkboxChecked: () => createIcon('check-square'),
  checkboxIndeterminate: () => createIcon('minus-square'),
  checkboxUnchecked: () => createIcon('square'),

  // Radio buttons
  radioButtonOn: () => createIcon('radio-button'),
  radioButtonOff: () => createIcon('circle')
})

/**
 * Get the icon configuration for AG Grid
 * Call this in grid options to replace default AG Grid icons with Phosphor wa-icon elements.
 *
 * @example
 * ```typescript
 * const gridOptions: GridOptions = {
 *   icons: createIconsConfig(),
 *   // ... other options
 * }
 * ```
 */
export { createIconsConfig as getGridIcons }
